import {
  getAllUnpaidInvoices,
  authenticateYuki,
  getYukiPayments,
  recordChargebeePayment,
} from 'api';
import NavigationBar from 'components/NavigationBar';
import { dateToDDMMYY_HHMM } from 'helpers';
import { useEffect, useState, useCallback } from 'react';
import { useAuthState } from 'stores/AuthStore';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faTimes,
  faSync,
  faLink,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';

// ─── Layout ──────────────────────────────────────────────────────────────────

const SPACING = 24;

const Container = styled.div`
  padding: 0 ${SPACING}px ${SPACING}px;
  background: ${({ theme }) => theme.background100};
  min-height: calc(100vh - 60px);
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  padding: ${SPACING}px 0 ${SPACING - 8}px;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.boldTextColor};
  margin: 0;
`;

// ─── Cards ───────────────────────────────────────────────────────────────────

const SectionCard = styled.div`
  background: ${({ theme }) => theme.background};
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: ${SPACING}px;
  border: 1px solid ${({ theme }) => theme.background200};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
`;

const SectionTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.mainTextColor};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const SectionCount = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.mainTextColor};
  background: ${({ theme }) => theme.background100};
  padding: 2px 10px;
  border-radius: 12px;
  margin-left: 8px;
`;

const ChevronIcon = styled(FontAwesomeIcon)`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.mainTextColor};
`;

// ─── Yuki Panel ──────────────────────────────────────────────────────────────

const YukiForm = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InputLabel = styled.label`
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.mainTextColor};
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.background300};
  border-radius: 6px;
  font-size: 0.85rem;
  background: ${({ theme }) => theme.background100};
  color: ${({ theme }) => theme.boldTextColor};
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.accentColor};
  }
`;

const FetchButton = styled.button<{ loading?: boolean }>`
  padding: 8px 20px;
  background: ${({ theme }) => theme.accentColor};
  color: ${({ theme }) => theme.boldTextColor};
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${({ loading }) => (loading ? 0.7 : 1)};
  pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};
  &:hover {
    opacity: 0.85;
  }
`;

const StatusText = styled.span<{ variant: 'idle' | 'loading' | 'success' | 'error' }>`
  font-size: 0.78rem;
  font-weight: 500;
  margin-left: 8px;
  color: ${({ variant }) => {
    const map = {
      idle: '#b0b5c1',
      loading: '#3b9af0',
      success: '#15ab90',
      error: '#e05a6a',
    };
    return map[variant];
  }};
`;

// ─── Table ───────────────────────────────────────────────────────────────────

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
`;

const TH = styled.th<{ align?: string }>`
  padding: 10px 14px;
  background: ${({ theme }) => theme.background100};
  border-bottom: 2px solid ${({ theme }) => theme.background300};
  text-align: ${({ align }) => align || 'left'};
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.mainTextColor};
  font-weight: 600;
  white-space: nowrap;
`;

const TR = styled.tr`
  &:hover td {
    background: ${({ theme }) => theme.background100};
  }
`;

const TD = styled.td<{ align?: string }>`
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.background200};
  color: ${({ theme }) => theme.boldTextColor};
  text-align: ${({ align }) => align || 'left'};
  font-size: 0.85rem;
  transition: background 0.1s;
`;

const MatchArrow = styled(TD)`
  text-align: center;
  color: ${({ theme }) => theme.mainTextColor};
  font-size: 0.75rem;
`;

const Badge = styled.span<{ color: string; bg: string }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  color: ${({ color }) => color};
  background: ${({ bg }) => bg};
  font-weight: 600;
`;

const AmountText = styled.span`
  font-weight: 500;
`;

const Description = styled.div`
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.mainTextColor};
`;

// ─── Action buttons ──────────────────────────────────────────────────────────

const ActionButton = styled.button<{ variant: 'confirm' | 'dismiss' }>`
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ variant }) => (variant === 'confirm' ? '#fff' : '#666')};
  background: ${({ variant }) =>
    variant === 'confirm' ? '#15ab90' : 'rgba(0,0,0,0.06)'};
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 6px;
`;

// ─── Side-by-side unmatched ──────────────────────────────────────────────────

const UnmatchedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACING}px;
  margin-top: 16px;
`;

const UnmatchedColumn = styled.div``;

const UnmatchedLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.mainTextColor};
  margin-bottom: 8px;
`;

// ─── Toast ───────────────────────────────────────────────────────────────────

const Toast = styled.div<{ variant: 'success' | 'error' }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #fff;
  background: ${({ variant }) => (variant === 'success' ? '#15ab90' : '#e05a6a')};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease;
  @keyframes slideIn {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

// ─── Types ───────────────────────────────────────────────────────────────────

interface YukiTransaction {
  id: string;
  transactionDate: string;
  description: string;
  amount: number;
  glAccountCode: string;
}

interface MatchedPair {
  transaction: YukiTransaction;
  invoice: any;
  confidence: 'exact' | 'amount' | 'reference';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseYukiXml = (xml: string): YukiTransaction[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'text/xml');
  const transactions = doc.getElementsByTagName('Transaction');
  const parsed: YukiTransaction[] = [];

  for (let i = 0; i < transactions.length; i++) {
    const tx = transactions[i];
    const getText = (tag: string) => tx.getElementsByTagName(tag)[0]?.textContent ?? '';

    parsed.push({
      id: getText('id'),
      transactionDate: getText('transactionDate'),
      description: getText('description'),
      amount: parseFloat(getText('amount')) || 0,
      glAccountCode: getText('glAccountCode'),
    });
  }

  // Filter to incoming payments only
  return parsed.filter((tx) => tx.glAccountCode === '550000');
};

const matchTransactions = (
  transactions: YukiTransaction[],
  invoices: any[]
): { matched: MatchedPair[]; unmatchedTx: YukiTransaction[]; unmatchedInv: any[] } => {
  const matched: MatchedPair[] = [];
  const usedTxIds = new Set<string>();
  const usedInvIds = new Set<string>();

  // Score all possible pairs
  const candidates: {
    tx: YukiTransaction;
    inv: any;
    confidence: 'exact' | 'amount' | 'reference';
    score: number;
  }[] = [];

  for (const tx of transactions) {
    for (const inv of invoices) {
      const invoiceAmount = inv.amount_due / 100;
      const amountMatch = Math.abs(tx.amount - invoiceAmount) < 0.02;
      const refMatch = tx.description.includes(inv.id);

      if (amountMatch && refMatch) {
        candidates.push({ tx, inv, confidence: 'exact', score: 3 });
      } else if (amountMatch) {
        candidates.push({ tx, inv, confidence: 'amount', score: 2 });
      } else if (refMatch) {
        candidates.push({ tx, inv, confidence: 'reference', score: 1 });
      }
    }
  }

  // Sort by score descending (exact first, then amount, then reference)
  candidates.sort((a, b) => b.score - a.score);

  // Greedily assign best matches
  for (const c of candidates) {
    if (usedTxIds.has(c.tx.id) || usedInvIds.has(c.inv.id)) continue;
    matched.push({ transaction: c.tx, invoice: c.inv, confidence: c.confidence });
    usedTxIds.add(c.tx.id);
    usedInvIds.add(c.inv.id);
  }

  const unmatchedTx = transactions.filter((tx) => !usedTxIds.has(tx.id));
  const unmatchedInv = invoices.filter((inv) => !usedInvIds.has(inv.id));

  return { matched, unmatchedTx, unmatchedInv };
};

const formatYukiDate = (dateStr: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return dateToDDMMYY_HHMM(d).split(',')[0]; // date only, no time
};

const toISODate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// ─── Component ───────────────────────────────────────────────────────────────

const InvoicingPage = () => {
  const { AuthState } = useAuthState();
  const { sessionToken } = AuthState;

  // Chargebee
  const [unpaidInvoices, setUnpaidInvoices] = useState<any[]>([]);

  // Yuki form
  const [accessKey, setAccessKey] = useState('');
  const [administrationID, setAdministrationID] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => toISODate(new Date()));

  // Yuki state
  const [yukiStatus, setYukiStatus] = useState<'idle' | 'authenticating' | 'fetching' | 'done' | 'error'>('idle');
  const [yukiError, setYukiError] = useState('');
  const [yukiPanelOpen, setYukiPanelOpen] = useState(true);

  // Matching
  const [matchedPairs, setMatchedPairs] = useState<MatchedPair[]>([]);
  const [unmatchedTransactions, setUnmatchedTransactions] = useState<YukiTransaction[]>([]);
  const [unmatchedInvoices, setUnmatchedInvoices] = useState<any[]>([]);

  // UI
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

  // Fetch unpaid invoices
  const fetchInvoices = useCallback(() => {
    if (!sessionToken) return;
    getAllUnpaidInvoices(sessionToken).then((response: any) => {
      setUnpaidInvoices(
        response.data.invoiceList.map((x: any) => ({ ...x.jsonObj }))
      );
    });
  }, [sessionToken]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Show toast
  const showToast = (message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
    setTimeout(() => setToast(null), 3000);
  };

  // Fetch Yuki payments
  const handleFetchYuki = async () => {
    if (!accessKey || !administrationID) return;

    setYukiStatus('authenticating');
    setYukiError('');

    try {
      // Step 1: authenticate
      const authRes = await authenticateYuki(sessionToken, accessKey);
      const sessionID = authRes.data.authenticateResult;

      if (!sessionID) {
        setYukiStatus('error');
        setYukiError('Authentication failed — no session returned');
        return;
      }

      // Step 2: fetch payments
      setYukiStatus('fetching');
      const paymentsRes = await getYukiPayments(
        sessionToken,
        sessionID,
        administrationID,
        startDate,
        endDate
      );

      const xml = paymentsRes.data.responseBody;
      if (!xml) {
        setYukiStatus('error');
        setYukiError('No payment data returned');
        return;
      }

      // Step 3: parse and match
      const transactions = parseYukiXml(xml);
      const { matched, unmatchedTx, unmatchedInv } = matchTransactions(
        transactions,
        unpaidInvoices
      );

      setMatchedPairs(matched);
      setUnmatchedTransactions(unmatchedTx);
      setUnmatchedInvoices(unmatchedInv);
      setYukiStatus('done');
    } catch (err: any) {
      setYukiStatus('error');
      setYukiError(err?.response?.data?.message || err?.message || 'Unknown error');
    }
  };

  // Confirm a match
  const handleConfirm = async (pair: MatchedPair) => {
    setConfirmingId(pair.transaction.id);
    try {
      await recordChargebeePayment(
        sessionToken,
        pair.invoice.id,
        pair.invoice.amount_due, // already in cents
        `Yuki payment ${pair.transaction.id} on ${pair.transaction.transactionDate}`
      );

      // Remove from matched list
      setMatchedPairs((prev) => prev.filter((p) => p.transaction.id !== pair.transaction.id));
      showToast(`Payment recorded for ${pair.invoice.id}`, 'success');

      // Refresh invoices
      fetchInvoices();
    } catch (err: any) {
      showToast(`Failed: ${err?.response?.data?.message || err?.message}`, 'error');
    } finally {
      setConfirmingId(null);
    }
  };

  // Dismiss a match
  const handleDismiss = (pair: MatchedPair) => {
    setMatchedPairs((prev) => prev.filter((p) => p.transaction.id !== pair.transaction.id));
    setUnmatchedTransactions((prev) => [...prev, pair.transaction]);
    setUnmatchedInvoices((prev) => [...prev, pair.invoice]);
  };

  const confidenceBadge = (c: 'exact' | 'amount' | 'reference') => {
    const map = {
      exact: { color: '#15ab90', bg: 'rgba(21,171,144,0.1)', label: 'Exact' },
      amount: { color: '#b8920a', bg: 'rgba(255,204,0,0.15)', label: 'Amount' },
      reference: { color: '#3b9af0', bg: 'rgba(99,179,245,0.15)', label: 'Reference' },
    };
    const { color, bg, label } = map[c];
    return <Badge color={color} bg={bg}>{label}</Badge>;
  };

  const statusLabel = () => {
    const map: Record<string, { text: string; variant: 'idle' | 'loading' | 'success' | 'error' }> = {
      idle: { text: '', variant: 'idle' },
      authenticating: { text: 'Authenticating with Yuki...', variant: 'loading' },
      fetching: { text: 'Fetching transactions...', variant: 'loading' },
      done: { text: `Found ${matchedPairs.length} matches`, variant: 'success' },
      error: { text: yukiError, variant: 'error' },
    };
    const s = map[yukiStatus];
    if (!s.text) return null;
    return <StatusText variant={s.variant}>{s.text}</StatusText>;
  };

  return (
    <Container>
      <NavigationBar />

      <PageHeader>
        <PageTitle>Invoice Payment Matching</PageTitle>
      </PageHeader>

      {/* ─── Yuki Connection Panel ────────────────────────────────────────── */}
      <SectionCard>
        <SectionHeader onClick={() => setYukiPanelOpen(!yukiPanelOpen)}>
          <SectionTitle>Yuki Connection</SectionTitle>
          <ChevronIcon icon={(yukiPanelOpen ? faChevronUp : faChevronDown) as any} />
        </SectionHeader>

        {yukiPanelOpen && (
          <YukiForm>
            <InputGroup>
              <InputLabel>Access Key</InputLabel>
              <Input
                type="password"
                value={accessKey}
                onChange={(e) => setAccessKey(e.target.value)}
                placeholder="Yuki access key"
                style={{ width: 220 }}
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>Administration ID</InputLabel>
              <Input
                value={administrationID}
                onChange={(e) => setAdministrationID(e.target.value)}
                placeholder="Administration ID"
                style={{ width: 220 }}
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>From</InputLabel>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>To</InputLabel>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </InputGroup>

            <FetchButton
              loading={yukiStatus === 'authenticating' || yukiStatus === 'fetching'}
              onClick={handleFetchYuki}
            >
              <FontAwesomeIcon icon={faSync as any} spin={yukiStatus === 'authenticating' || yukiStatus === 'fetching'} />
              Fetch Payments
            </FetchButton>

            {statusLabel()}
          </YukiForm>
        )}
      </SectionCard>

      {/* ─── Matched Payments ─────────────────────────────────────────────── */}
      {matchedPairs.length > 0 && (
        <SectionCard>
          <SectionTitle>
            Matched Payments
            <SectionCount>{matchedPairs.length}</SectionCount>
          </SectionTitle>

          <Table>
            <thead>
              <tr>
                <TH>Yuki Date</TH>
                <TH>Description</TH>
                <TH align="right">Yuki Amount</TH>
                <TH></TH>
                <TH>Invoice ID</TH>
                <TH>Company</TH>
                <TH align="right">Invoice Amount</TH>
                <TH>Confidence</TH>
                <TH></TH>
              </tr>
            </thead>
            <tbody>
              {matchedPairs.map((pair) => (
                <TR key={pair.transaction.id}>
                  <TD>{formatYukiDate(pair.transaction.transactionDate)}</TD>
                  <TD>
                    <Description title={pair.transaction.description}>
                      {pair.transaction.description}
                    </Description>
                  </TD>
                  <TD align="right">
                    <AmountText>&euro;{pair.transaction.amount.toFixed(2)}</AmountText>
                  </TD>
                  <MatchArrow>
                    <FontAwesomeIcon icon={faLink as any} />
                  </MatchArrow>
                  <TD>{pair.invoice.id}</TD>
                  <TD>{pair.invoice?.billing_address?.company ?? '—'}</TD>
                  <TD align="right">
                    <AmountText>&euro;{(pair.invoice.amount_due / 100).toFixed(2)}</AmountText>
                  </TD>
                  <TD>{confidenceBadge(pair.confidence)}</TD>
                  <TD>
                    <ActionGroup>
                      <ActionButton
                        variant="confirm"
                        onClick={() => handleConfirm(pair)}
                        disabled={confirmingId === pair.transaction.id}
                      >
                        <FontAwesomeIcon icon={faCheck as any} />
                        {confirmingId === pair.transaction.id ? 'Recording...' : 'Confirm'}
                      </ActionButton>
                      <ActionButton variant="dismiss" onClick={() => handleDismiss(pair)}>
                        <FontAwesomeIcon icon={faTimes as any} />
                      </ActionButton>
                    </ActionGroup>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </SectionCard>
      )}

      {/* ─── Unmatched ────────────────────────────────────────────────────── */}
      {yukiStatus === 'done' && (unmatchedTransactions.length > 0 || unmatchedInvoices.length > 0) && (
        <SectionCard>
          <SectionTitle>Unmatched</SectionTitle>

          <UnmatchedGrid>
            <UnmatchedColumn>
              <UnmatchedLabel>Yuki Transactions ({unmatchedTransactions.length})</UnmatchedLabel>
              {unmatchedTransactions.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <TH>Date</TH>
                      <TH>Description</TH>
                      <TH align="right">Amount</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedTransactions.map((tx) => (
                      <TR key={tx.id}>
                        <TD>{formatYukiDate(tx.transactionDate)}</TD>
                        <TD>
                          <Description title={tx.description}>
                            {tx.description}
                          </Description>
                        </TD>
                        <TD align="right">
                          <AmountText>&euro;{tx.amount.toFixed(2)}</AmountText>
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div style={{ color: '#b0b5c1', fontSize: '0.85rem' }}>All transactions matched</div>
              )}
            </UnmatchedColumn>

            <UnmatchedColumn>
              <UnmatchedLabel>Unpaid Invoices ({unmatchedInvoices.length})</UnmatchedLabel>
              {unmatchedInvoices.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <TH>Invoice ID</TH>
                      <TH>Company</TH>
                      <TH align="right">Amount</TH>
                      <TH>Date</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedInvoices
                      .sort((a: any, b: any) => b.date - a.date)
                      .map((inv: any) => (
                        <TR key={inv.id}>
                          <TD>{inv.id}</TD>
                          <TD>{inv?.billing_address?.company ?? '—'}</TD>
                          <TD align="right">
                            <AmountText>&euro;{(inv.amount_due / 100).toFixed(2)}</AmountText>
                          </TD>
                          <TD>{dateToDDMMYY_HHMM(new Date(inv.date * 1000))}</TD>
                        </TR>
                      ))}
                  </tbody>
                </Table>
              ) : (
                <div style={{ color: '#b0b5c1', fontSize: '0.85rem' }}>All invoices matched</div>
              )}
            </UnmatchedColumn>
          </UnmatchedGrid>
        </SectionCard>
      )}

      {/* ─── All Unpaid Invoices (always visible) ─────────────────────────── */}
      <SectionCard>
        <SectionTitle>
          All Unpaid Invoices
          <SectionCount>{unpaidInvoices.length}</SectionCount>
        </SectionTitle>

        <Table>
          <thead>
            <tr>
              <TH>#</TH>
              <TH>Invoice ID</TH>
              <TH>Company</TH>
              <TH align="right">Amount</TH>
              <TH>Date</TH>
            </tr>
          </thead>
          <tbody>
            {unpaidInvoices
              .sort((a: any, b: any) => b.date - a.date)
              .map((invoice: any, index: number) => (
                <TR key={invoice.id}>
                  <TD>{index + 1}</TD>
                  <TD>{invoice.id}</TD>
                  <TD>{invoice?.billing_address?.company ?? '—'}</TD>
                  <TD align="right">
                    <AmountText>&euro;{(invoice.amount_due / 100).toFixed(2)}</AmountText>
                  </TD>
                  <TD>{dateToDDMMYY_HHMM(new Date(invoice.date * 1000))}</TD>
                </TR>
              ))}
          </tbody>
        </Table>
      </SectionCard>

      {/* ─── Toast ────────────────────────────────────────────────────────── */}
      {toast && <Toast variant={toast.variant}>{toast.message}</Toast>}
    </Container>
  );
};

export default InvoicingPage;
